import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  images: string[];
  initialIndex: number;
  onClose: () => void;
  alt?: string;
}

const ProductLightbox: React.FC<Props> = ({ images, initialIndex, onClose, alt = '' }) => {
  const [idx, setIdx] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);

  const prev = () => { setIdx((i) => (i - 1 + images.length) % images.length); setZoom(1); };
  const next = () => { setIdx((i) => (i + 1) % images.length); setZoom(1); };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === '+' || e.key === '=') setZoom((z) => Math.min(3, z + 0.5));
      if (e.key === '-') setZoom((z) => Math.max(1, z - 0.5));
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [touchStart, setTouchStart] = useState<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => setTouchStart(e.touches[0].clientX);
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStart === null) return;
    const dx = e.changedTouches[0].clientX - touchStart;
    if (Math.abs(dx) > 50) { dx > 0 ? prev() : next(); }
    setTouchStart(null);
  };

  const content = (
    <div className="pd-lightbox" role="dialog" aria-modal="true" onClick={onClose}>
      <button className="pd-lightbox__close" aria-label="Zavřít" onClick={onClose}>×</button>
      {images.length > 1 && (
        <>
          <button className="pd-lightbox__prev" aria-label="Předchozí" onClick={(e) => { e.stopPropagation(); prev(); }}>‹</button>
          <button className="pd-lightbox__next" aria-label="Další" onClick={(e) => { e.stopPropagation(); next(); }}>›</button>
        </>
      )}
      <div className="pd-lightbox__stage" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} onClick={(e) => e.stopPropagation()}>
        <img
          className="pd-lightbox__image"
          src={images[idx]}
          alt={alt}
          style={{ transform: `scale(${zoom})` }}
          onClick={(e) => { e.stopPropagation(); setZoom((z) => (z >= 2.5 ? 1 : z + 0.5)); }}
        />
      </div>
      <div className="pd-lightbox__zoom" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => setZoom((z) => Math.max(1, z - 0.5))} aria-label="Oddálit">−</button>
        <span>{Math.round(zoom * 100)} %</span>
        <button onClick={() => setZoom((z) => Math.min(3, z + 0.5))} aria-label="Přiblížit">+</button>
      </div>
      {images.length > 1 && (
        <div className="pd-lightbox__counter">{idx + 1} / {images.length}</div>
      )}
    </div>
  );

  // Portál do <body> ať lightbox není uvězněn v sticky/overflow rodičovi
  if (typeof document === 'undefined') return null;
  return createPortal(content, document.body);
};

export default ProductLightbox;
